import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { memo, useState } from "react";
import PropTypes from "prop-types";

import { wrapFormikHandleChange } from "../../../../../../infrastructure/utils/formatters";
import FollowUpReminderBanner from "../../../../../components/request/FollowUpReminderBanner";
import RequestFollowUpTimeline from "../../../../../components/request/RequestFollowUpTimeline";
import { RequestActivityFeedTab } from "../../../../../components/request/case-tabs";
import AssignmentOperatorHelp from "../../../../../components/request/AssignmentOperatorHelp";
import CustomerInfoCard from "./CustomerInfoCard";
import LocationCard from "./LocationCard";
import MediaGalleryCard from "./MediaGalleryCard";
import ProjectInfoCard from "./ProjectInfoCard";
import OperatorLoanSummaryCard from "./OperatorLoanSummaryCard";
import CreditInquiryCard from "./CreditInquiryCard";
import LoanGuarantorCard from "./LoanGuarantorCard";
import OperatorReviewHero from "./OperatorReviewHero";
import RequestOperatorActionTimeline from "./RequestOperatorActionTimeline";
import RequestTabs, { REQUEST_TAB_IDS } from "./RequestTabs";
import ReviewActionsPanel from "./ReviewActionsPanel";
import { FULL_WIDTH_STACK_SX, PAGE_STACK_SPACING } from "./initialReviewStyles";
import {
  OPERATOR_MAIN_COLUMN_SX,
  OPERATOR_PAGE_SHELL_SX,
  OPERATOR_SECTION_TITLE_SX,
} from "./operatorReviewStyles";

const HiddenFields = memo(({ handleChange, onMakeFormikVariable }) => (
  <Box sx={{ display: "none" }}>
    {["companyId", "userId", "stepInfoId", "provinceId"].map((name) => (
      <TextField
        key={name}
        onChange={wrapFormikHandleChange(handleChange)}
        {...onMakeFormikVariable(name)}
      />
    ))}
  </Box>
));

HiddenFields.displayName = "HiddenFields";

/**
 * Linear operator review: edit sections top→bottom, then decide.
 * No duplicate read-only summary of the same fields.
 */
const InitialReviewWorkspace = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  request,
  formData,
  data,
  fieldDisabled,
  isEditable,
  fileBaseUrl,
  followUps,
  actionHistory,
  onRegisterFollowUp,
  onMakeFormikVariable,
  handleFileChange,
  powerPlantTypeList,
  workflowStageTitle,
  workflowSource,
  requestInfoId,
  detailApi,
  assignmentOwnerLabel,
  assignmentFeatureEnabled,
  assignmentFeatureChecked,
  canClaimRequests,
  statusUserKind,
  statusUserRoleName,
  canShowClaim,
  canRelease,
  assignmentLoading,
  onClaim,
  onRelease,
  loading,
  onNavigateToSection = null,
  hideReferAction = false,
  hideReviseAction = false,
  dataOnlyEditMode = false,
  onQuickStatusSubmit = null,
  distributionCompanies = [],
  creditInquiryApiPath = "",
}) => {
  const [secondaryTab, setSecondaryTab] = useState(REQUEST_TAB_IDS.CONVERSATIONS);
  const [creditInquiryRefreshToken, setCreditInquiryRefreshToken] = useState(0);

  return (
    <Box sx={OPERATOR_PAGE_SHELL_SX}>
      <HiddenFields
        handleChange={handleChange}
        onMakeFormikVariable={onMakeFormikVariable}
      />

      <Stack spacing={PAGE_STACK_SPACING}>
        {!isEditable ? (
          <Alert
            severity="info"
            action={
              onNavigateToSection ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => onNavigateToSection("loan-mehrsan")}
                >
                  رفتن به وام / مهرسان
                </Button>
              ) : null
            }
          >
            این درخواست از مرحله بررسی اولیه خارج شده و فقط برای مشاهده است.
          </Alert>
        ) : null}

        <AssignmentOperatorHelp
          showAssignmentUi={assignmentFeatureEnabled}
          featureChecked={assignmentFeatureChecked}
          canClaimRequests={canClaimRequests}
          currentUserKind={statusUserKind}
          currentUserRoleName={statusUserRoleName}
          hasClaimableRow={canShowClaim}
          compact
        />

        {onRegisterFollowUp ? (
          <FollowUpReminderBanner
            followUps={followUps}
            onRegisterClick={onRegisterFollowUp}
            compact
          />
        ) : null}

        <Box sx={OPERATOR_MAIN_COLUMN_SX}>
          <Stack spacing={PAGE_STACK_SPACING} sx={FULL_WIDTH_STACK_SX}>
            <OperatorReviewHero
              values={values}
              workflowStageTitle={workflowStageTitle}
              assignmentOwnerLabel={assignmentOwnerLabel}
              compact
            />

            <Typography sx={OPERATOR_SECTION_TITLE_SX}>
              ۱. اطلاعات متقاضی
            </Typography>
            <CustomerInfoCard
              values={values}
              fieldDisabled={fieldDisabled}
              onMakeFormikVariable={onMakeFormikVariable}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
            />

            <Typography sx={OPERATOR_SECTION_TITLE_SX}>
              ۲. اطلاعات پروژه و نیروگاه
            </Typography>
            <ProjectInfoCard
              values={values}
              formData={formData}
              isEditable={isEditable}
              fieldDisabled={fieldDisabled}
              onMakeFormikVariable={onMakeFormikVariable}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              powerPlantTypeList={powerPlantTypeList}
            />

            <OperatorLoanSummaryCard values={values} />

            <LoanGuarantorCard
              requestInfoId={requestInfoId}
              apiPath={creditInquiryApiPath}
              needsLoan={Boolean(values?.needsLoan)}
              loanGuaranteeChequeIntent={
                values?.loanGuaranteeChequeIntent ?? values?.LoanGuaranteeChequeIntent
              }
              isEditable={isEditable}
              onSaved={() =>
                setCreditInquiryRefreshToken((token) => token + 1)
              }
            />

            <CreditInquiryCard
              requestInfoId={requestInfoId}
              apiPath={creditInquiryApiPath}
              needsLoan={Boolean(values?.needsLoan)}
              loanGuaranteeChequeIntent={
                values?.loanGuaranteeChequeIntent ?? values?.LoanGuaranteeChequeIntent
              }
              isEditable={isEditable}
              refreshToken={creditInquiryRefreshToken}
            />

            <Typography sx={OPERATOR_SECTION_TITLE_SX}>
              ۳. موقعیت و آدرس
            </Typography>
            <LocationCard
              values={values}
              data={data}
              formData={formData}
              fieldDisabled={fieldDisabled}
              onMakeFormikVariable={onMakeFormikVariable}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
              errors={errors}
              touched={touched}
              distributionCompanies={distributionCompanies}
            />

            <Typography sx={OPERATOR_SECTION_TITLE_SX}>
              ۴. تصاویر و مدارک
            </Typography>
            <MediaGalleryCard
              values={values}
              fileBaseUrl={fileBaseUrl}
              fieldDisabled={fieldDisabled}
              handleFileChange={handleFileChange}
              errors={errors}
              touched={touched}
              layout="grid"
            />

            <Typography sx={OPERATOR_SECTION_TITLE_SX}>
              ۵. تصمیم نهایی کارشناس
            </Typography>
            <ReviewActionsPanel
              values={values}
              formData={formData}
              isEditable={isEditable}
              fieldDisabled={fieldDisabled}
              isSubmitting={isSubmitting}
              loading={loading}
              onMakeFormikVariable={onMakeFormikVariable}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              assignmentOwnerLabel={assignmentOwnerLabel}
              assignedAt={request?.assignedAt}
              workflowStageTitle={workflowStageTitle}
              requestConfirmList={formData?.requestConfirm}
              onClaim={onClaim}
              onRelease={onRelease}
              canClaim={canShowClaim}
              canRelease={canRelease}
              assignmentLoading={assignmentLoading}
              hideReferAction={hideReferAction}
              hideReviseAction={hideReviseAction}
              dataOnlyEditMode={dataOnlyEditMode}
              onQuickStatusSubmit={onQuickStatusSubmit}
              compact
            />

            <Box>
              <Typography sx={{ ...OPERATOR_SECTION_TITLE_SX, mb: 1 }}>
                گفتگوها و تاریخچه
              </Typography>
              <RequestTabs
                activeTab={secondaryTab}
                onChange={setSecondaryTab}
                tabs={[
                  {
                    id: REQUEST_TAB_IDS.CONVERSATIONS,
                    label: "گفتگوها",
                  },
                  {
                    id: REQUEST_TAB_IDS.HISTORY,
                    label: "تاریخچه",
                  },
                ]}
              >
                {secondaryTab === REQUEST_TAB_IDS.CONVERSATIONS ? (
                  <RequestActivityFeedTab
                    requestInfoId={Number(requestInfoId)}
                    detailApi={detailApi}
                    fileBaseUrl={fileBaseUrl}
                    active
                    followUps={followUps}
                  />
                ) : null}

                {secondaryTab === REQUEST_TAB_IDS.HISTORY ? (
                  <Stack spacing={PAGE_STACK_SPACING}>
                    <RequestOperatorActionTimeline items={actionHistory} />
                    {followUps?.length ? (
                      <RequestFollowUpTimeline followUps={followUps} />
                    ) : null}
                  </Stack>
                ) : null}
              </RequestTabs>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default memo(InitialReviewWorkspace);

InitialReviewWorkspace.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  setFieldValue: PropTypes.func.isRequired,
  request: PropTypes.object,
  formData: PropTypes.object,
  data: PropTypes.object,
  fieldDisabled: PropTypes.bool,
  isEditable: PropTypes.bool,
  fileBaseUrl: PropTypes.string,
  followUps: PropTypes.array,
  actionHistory: PropTypes.array,
  onRegisterFollowUp: PropTypes.func,
  onMakeFormikVariable: PropTypes.func.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  powerPlantTypeList: PropTypes.array,
  workflowStageTitle: PropTypes.string,
  workflowSource: PropTypes.string,
  requestInfoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  detailApi: PropTypes.string,
  assignmentOwnerLabel: PropTypes.string,
  assignmentFeatureEnabled: PropTypes.bool,
  assignmentFeatureChecked: PropTypes.bool,
  canClaimRequests: PropTypes.bool,
  statusUserKind: PropTypes.string,
  statusUserRoleName: PropTypes.string,
  canShowClaim: PropTypes.bool,
  canRelease: PropTypes.bool,
  assignmentLoading: PropTypes.bool,
  onClaim: PropTypes.func,
  onRelease: PropTypes.func,
  loading: PropTypes.bool,
  onNavigateToSection: PropTypes.func,
  hideReferAction: PropTypes.bool,
  hideReviseAction: PropTypes.bool,
  dataOnlyEditMode: PropTypes.bool,
  onQuickStatusSubmit: PropTypes.func,
  distributionCompanies: PropTypes.array,
  creditInquiryApiPath: PropTypes.string,
};
